import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ArrowLeft, Home } from 'lucide-react';
import { SmallText } from './BrandTypography';

/**
 * QuickExit Component
 * 
 * A universal quick-exit pattern for all relational modules.
 * Provides a visible, gentle, and emotionally safe way to leave.
 * 
 * Placement: Top-right corner of every screen
 * Behavior: Immediately returns user without confirmation
 * Design: Soft icon, low-contrast, no harsh animations
 */

export const QuickExitButton = ({
    onExit,
    destination = '/relationship-hub',
    variant = 'text', // 'text', 'x', 'arrow', 'home'
    label = 'Leave for now',
    className = ''
}) => {
    const navigate = useNavigate();

    const handleExit = () => {
        if (onExit) {
            onExit();
        } else {
            navigate(destination);
        }
    };

    const icons = {
        x: X,
        arrow: ArrowLeft,
        home: Home
    };

    // Text variant - softer, more inviting exit
    if (variant === 'text') {
        return (
            <button
                onClick={handleExit}
                className={`
                    px-2 sm:px-3 py-1.5 rounded-full flex items-center gap-1
                    text-[var(--orenda-text-muted)] hover:text-[var(--orenda-text-secondary)] 
                    hover:bg-[var(--orenda-border)]/30
                    text-xs sm:text-sm transition-all duration-200 whitespace-nowrap
                    ${className}
                `}
                data-testid="quick-exit-btn"
            >
                <span className="hidden sm:inline">{label}</span>
                <span className="sm:hidden">Exit</span>
            </button>
        );
    }

    const Icon = icons[variant] || X;

    return (
        <button
            onClick={handleExit}
            className={`
                w-11 h-11 rounded-full flex items-center justify-center
                text-[var(--orenda-text-muted)] hover:text-[var(--orenda-text-secondary)] 
                hover:bg-[var(--orenda-border)]/30
                transition-all duration-200
                ${className}
            `}
            title={label}
            data-testid="quick-exit-btn"
        >
            <Icon className="w-5 h-5" />
        </button>
    );
};

/**
 * QuickExitFooter Component
 * 
 * A footer-based exit option for additional visibility.
 * Uses soft, non-alarming language.
 */
export const QuickExitFooter = ({
    onExit,
    destination = '/relationship-hub',
    label = 'Pause',
    showIcon = true,
    className = ''
}) => {
    const navigate = useNavigate();

    const handleExit = () => {
        if (onExit) {
            onExit();
        } else {
            navigate(destination);
        }
    };

    return (
        <div className={`text-center ${className}`}>
            <button
                onClick={handleExit}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                           text-[#FFBB98] hover:text-[#FFBB98] hover:bg-[#FFBB98]/10
                           text-sm font-medium transition-all duration-200"
                data-testid="footer-exit-btn"
            >
                {showIcon && <X className="w-4 h-4" />}
                {label}
            </button>
        </div>
    );
};

/**
 * SafetyFooter Component
 * 
 * Complete footer with exit option and reassurance message.
 */
export const SafetyFooter = ({
    onExit,
    destination = '/relationship-hub',
    exitLabel = 'Come back later',
    reassurance = 'You can pause anytime.',
    className = ''
}) => {
    const navigate = useNavigate();

    const handleExit = () => {
        if (onExit) {
            onExit();
        } else {
            navigate(destination);
        }
    };

    return (
        <div className={`mt-10 space-y-3 ${className}`}>
            {reassurance && (
                <SmallText className="text-[var(--orenda-text-muted)] text-center block italic">
                    {reassurance}
                </SmallText>
            )}
            <div className="text-center">
                <button
                    onClick={handleExit}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                               text-[var(--orenda-text-muted)] hover:text-[var(--orenda-text-secondary)] 
                               hover:bg-[var(--orenda-border)]/30
                               text-sm transition-all duration-200"
                    data-testid="safety-exit-btn"
                >
                    {exitLabel}
                </button>
            </div>
        </div>
    );
};

/**
 * ModuleHeader Component
 * 
 * Standard header with back button, title, and quick exit.
 * Ensures consistent placement across all modules.
 */
export const ModuleHeader = ({
    title,
    onBack,
    onExit,
    backDestination = '/relationship-hub',
    exitDestination = '/dashboard',
    showBack = true,
    showExit = true,
    rightContent = null,
    className = ''
}) => {
    const navigate = useNavigate();

    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigate(backDestination);
        }
    };

    const handleExit = () => {
        if (onExit) {
            onExit();
        } else {
            navigate(exitDestination);
        }
    };

    return (
        <header className={`
            sticky top-0 z-50 glass-header
            ${className}
        `}>
            <div className="max-w-xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
                {/* Back Button */}
                {showBack ? (
                    <button
                        onClick={handleBack}
                        className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center
                                   text-[#7D8E95] hover:text-[#1D6B6B] hover:bg-[#FFBB98]/40
                                   transition-all duration-200 flex-shrink-0"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                ) : (
                    <div className="w-9 sm:w-10" />
                )}

                {/* Title */}
                <h1 className="font-medium text-[#1D6B6B] text-sm sm:text-base truncate mx-2">
                    {title}
                </h1>

                {/* Right Content - Sound toggle, Quick Exit, or placeholder */}
                {rightContent ? (
                    rightContent
                ) : showExit ? (
                    <QuickExitButton 
                        onExit={handleExit}
                        destination={exitDestination}
                    />
                ) : (
                    <div className="w-9 sm:w-10" />
                )}
            </div>
        </header>
    );
};

export default QuickExitButton;
